const _ = require('../../../lodash');
const getConstants = require('../../../utils/getConstants');
const constants = getConstants();
const moment = require('moment-timezone');

const { formItemTypes, repeaterFormItemTypes, dataIntentFlags, locationAssignmentTypes } = constants;

const { newGuid } = require('../../../utils');
const classifyRepeaterEntryMode = require('../../forms/utilities/classifyRepeaterEntryMode');
const { kinds: repeaterEntryModeKinds } = classifyRepeaterEntryMode;

/*

questions: [
      {
         id: '018498D0-5DC8-78F8-B050-E25AEFD36E48',
         hint: 'Wipe down the surfaces of all tables',
         label: 'Clean the tables',
         idParent: null,
         resources: [],
         idItemType: 1,
         idRequiredType: 0,
         state: 0,
         idFormItem: 1,
         response: true         
      },
      {
         id: 2,
         label: 'Wash the walls',
         idParent: null,
         resources: [],
         idItemType: 1,
         idRequiredType: 1
      },
      {
         id: 7,
         label: 'Describe the state of your area',
         idParent: null,
         resources: [],
         idItemType: 4,
         idRequiredType: 0
      }
   ],

*/
class QuestionBuilder {
   constructor(territoryDomainService) {
      this.territoryDomainService = territoryDomainService;
   }

   build({ noLocation, formSnapshot, submissionSnapshot, resources, locations, datasources }) {
      this.guards({ noLocation, formSnapshot, submissionSnapshot, resources, locations });

      let questions = [];

      let resourceMedia = this.buildLocationSpecificResouceMedia({ formSnapshot, resources, locations, noLocation });

      let datasourceData = this.buildLocationSpecificDatasourceData({ formSnapshot, datasources, locations, noLocation });

      if (submissionSnapshot && submissionSnapshot.items.length > 0) {
         _.each(submissionSnapshot.items, (i) => {
            const fi = _.find(formSnapshot.items, (x) => {
               return x.id === i.idFormItem;
            });
            const eq = this.buildExistingQuestion({ questions, formItem: fi, submissionItem: i, resourceMedia, datasourceData });
            if (eq != null) {
               questions.push(eq);
            }
         });
      } else {
         _.each(formSnapshot.items, (i) => {
            const nq = this.buildQuestion({ questions, formItem: i, resourceMedia, datasourceData });
            if (nq != null) {
               questions.push(nq);
            }
         });
      }

      return {
         questions
      };
   }

   buildNewRepeaterGroupQuestions({ idItemRepeater, repeaterChildItems, noLocation, formSnapshot, resources, locations, datasources }) {
      this.guards({ noLocation, formSnapshot, submissionSnapshot: null, resources, locations });

      let questions = [];

      let resourceMedia = this.buildLocationSpecificResouceMedia({ formSnapshot, resources, locations, noLocation });

      let datasourceData = this.buildLocationSpecificDatasourceData({ formSnapshot, datasources, locations, noLocation });

      _.each(repeaterChildItems, (i) => {
         const nq = this.buildQuestion({ questions, formItem: i, resourceMedia, datasourceData, idRepeaterToIgnore: idItemRepeater });
         if (nq != null) {
            questions.push(nq);
         }
      });

      return {
         questions
      };
   }

   materializeFullListRepeaters({ questions, formSnapshot, noLocation, resources, locations, datasources }) {
      const materializedQuestions = questions || [];
      const formItems = formSnapshot?.items || [];

      const getChildFormItems = (idFormItem) => {
         let found = [];
         const children = _.filter(formItems, (item) => item.idParent == idFormItem);
         _.each(children, (child) => {
            found.push(child);
            found = found.concat(getChildFormItems(child.id));
         });
         return found;
      };

      const materializeRepeater = (repeaterQuestion) => {
         if (!repeaterQuestion || repeaterQuestion.idItemType != formItemTypes.REPEATER.id) return;

         const options = repeaterQuestion.options || {};
         const classification = classifyRepeaterEntryMode({
            idRepeaterType: options.idRepeaterType,
            entryMode: options.entryMode,
            idRepeaterCadence: options.idRepeaterCadence,
            timesToRepeat: options.timesToRepeat
         });

         if (classification.kind !== repeaterEntryModeKinds.CANONICAL_FULL_LIST || repeaterQuestion.repeaterMaterialized === true) {
            return;
         }

         const binding = options.binding || {};
         const hasDatasource = _.some(datasources || [], (datasource) => datasource.noDataSource == binding.noDataSource);
         if (!hasDatasource) {
            throw new Error(
               `Cannot materialize Full list Repeater ${repeaterQuestion.idFormItem}: datasource ${binding.noDataSource} is unavailable.`
            );
         }

         const idField = binding.idField || 'id';
         const txtField = binding.txtField || 'txt';
         const rows = Array.isArray(options.listOptions) ? options.listOptions : [];
         const childItems = getChildFormItems(repeaterQuestion.idFormItem);

         _.each(rows, (row) => {
            const sourceId = row?.[idField];
            if (sourceId == null) {
               throw new Error(`Cannot materialize Full list Repeater ${repeaterQuestion.idFormItem}: datasource row has no ${idField}.`);
            }

            const idRepeaterGroup = newGuid();
            const { questions: childQuestions } = this.buildNewRepeaterGroupQuestions({
               idItemRepeater: repeaterQuestion.idFormItem,
               repeaterChildItems: childItems,
               noLocation,
               formSnapshot,
               resources,
               locations,
               datasources
            });

            const group = {
               idItemType: formItemTypes.REPEATER_GROUP.id,
               idFormItem: repeaterQuestion.idFormItem,
               state: 0,
               label: row?.[txtField] == null ? null : row[txtField].toString(),
               hint: null,
               idRequiredType: 0,
               resources: [],
               idParent: repeaterQuestion.id,
               id: idRepeaterGroup,
               options: {
                  isSubform: true,
                  isSubformLocked: true
               },
               response: { no: 1, upd: moment.utc().toDate(), val: sourceId.toString() }
            };

            _.each(childQuestions, (question) => {
               if (question.idParent == null) {
                  question.idParent = group.id;
               }
            });

            materializedQuestions.push(group, ...childQuestions);
            _.each(childQuestions, materializeRepeater);
         });

         repeaterQuestion.repeaterMaterialized = true;
      };

      _.each(materializedQuestions.slice(), materializeRepeater);
      return { questions: materializedQuestions };
   }

   // Builds the necessary data which apply to the items on this form for this location
   buildLocationSpecificDatasource({ datasource, locations, noLocation }) {
      this.territoryDomainService.decorateWithLocationNos({ locations });
      const mergedById = {};

      const applicable = [];
      _.each(datasource.partitions || [], (part, index) => {
         const assignedLocations = this.territoryDomainService.determineAssignedLocations({
            locations,
            assignment: part.where
         });

         const isApplicable = _.some(assignedLocations, (al) => al.no === noLocation);
         if (!isApplicable) return;

         const intent = typeof part.intent === 'number' ? part.intent : 0;
         const isWriteback = (intent & dataIntentFlags.WRITEBACK) === dataIntentFlags.WRITEBACK;
         const isAllLocations =
            part.where && (part.where.all === true || part.where.noLocationAssignmentType === locationAssignmentTypes.ALL_LOCATIONS.id);
         const isSpecificLocation =
            part.where &&
            part.where.noLocationAssignmentType === locationAssignmentTypes.SPECIFIC_LOCATION.id &&
            _.some(part.where.specific || [], (x) => x && x.no === noLocation);

         // Preserve existing ordering within a semantic layer, but make cross-layer precedence deterministic:
         // CATALOG/base < Global WRITEBACK < Location WRITEBACK.
         let layerRank = 0;
         if (isWriteback) layerRank = isSpecificLocation ? 2 : isAllLocations ? 1 : 1;

         applicable.push({ part, index, layerRank });
      });

      applicable.sort((a, b) => {
         if (a.layerRank !== b.layerRank) return a.layerRank - b.layerRank;
         return a.index - b.index;
      });

      _.each(applicable, ({ part }) => {
         _.each(part.rows || [], (row) => {
            const data = row?.data;
            const id = data?.id;
            if (id == null) return;
            mergedById[id] = { ...(mergedById[id] ?? {}), ...data };
         });
      });

      return {
         noDataSource: datasource.noDataSource,
         name: datasource.name,
         rows: _.values(mergedById)
      };
   }

   buildLocationSpecificDatasourceData({ formSnapshot, datasources, locations, noLocation }) {
      this.territoryDomainService.decorateWithLocationNos({ locations });

      const itemDatasources = _.filter(
         _.map(formSnapshot.items, (item) => {
            const options = item.options || {};
            const isDormantFreeFlowRepeater =
               item.idItemType === formItemTypes.REPEATER.id &&
               (options.idRepeaterType === repeaterFormItemTypes.FREE_FLOW.id ||
                  options.idRepeaterType === String(repeaterFormItemTypes.FREE_FLOW.id));

            return isDormantFreeFlowRepeater ? null : options.binding;
         }),
         (binding) => binding != null
      );

      const applicableDatasources = _.filter(datasources, (r) => {
         return _.some(itemDatasources, (ir) => ir.noDataSource == r.noDataSource);
      });

      return _.map(applicableDatasources, (datasource) => {
         return this.buildLocationSpecificDatasource({ datasource, locations, noLocation });
      });
   }

   // Builds the necessary assets which apply to the items on this form for this location
   buildLocationSpecificResouceMedia({ formSnapshot, resources, locations, noLocation }) {
      // ensure all the locations are stamped with their partent locations
      // so the locations can be properly matched to the resource partitions where assignment
      this.territoryDomainService.decorateWithLocationNos({ locations });

      // no point in determining if resources are matched to this location
      // where they arent a part of any items on this form
      const itemResources = _.flatten(_.map(formSnapshot.items, 'resources'));
      const applicableResources = _.filter(resources, (r) => {
         return _.some(itemResources, (ir) => {
            return ir.noResource == r.noResource;
         });
      });

      // Where the resources are matched to this location
      // flatten them into a usable structure
      /*
         {
            id: '01862c36-bcd5-798a-eccc-e7333397ac09',
            asset: {
               no: '593',
               originalName: 'cleaning instructions',
               fullUrl: 'http://localhost:9091/cloudstack?origin_url=https://res.cloudinary.com/dxjdzdwfu/image/upload/v1675778559/t3/cleaning_instructions_dy1umy.pdf',
               extension: '.pdf',
               mimeType: 'application/pdf',
               mediaType: 'formResource'
            },
            noResource: '33',
            name: 'Cleaning Instructions',
            noResourceType: 1,
            noPartition: 0
         }
      */

      const resourceMedia = _.reduce(
         applicableResources,
         (result, resource) => {
            _.each(resource.partitions, (part) => {
               const assignedLocations = this.territoryDomainService.determineAssignedLocations({ locations, assignment: part.where });

               const isApplicable = _.some(assignedLocations, (al) => {
                  return al.no === noLocation;
               });

               if (isApplicable) {
                  _.each(part.rows, (row) => {
                     let locationResourceAsset = _.merge(
                        { id: row.id, asset: row.asset },
                        _.pick(resource, ['noResource', 'name', 'noResourceType']),
                        {
                           noPartition: part.no
                        }
                     );

                     result.push(locationResourceAsset);
                  });
               }
            });

            return result;
         },
         []
      );

      return resourceMedia;
   }

   buildExistingQuestion({ questions, submissionItem, formItem, resourceMedia, datasourceData }) {
      if (!formItem) {
         throw new Error("Can't build a submission item we're there is no corresponding form item");
      }

      var formQuestion = this.buildQuestion({ questions, formItem, submissionItem, resourceMedia, datasourceData });

      formQuestion.label = submissionItem.label == null ? formQuestion.label : submissionItem.label;
      formQuestion.hint = submissionItem.hint == null ? formQuestion.hint : submissionItem.hint;
      formQuestion.idRequiredType = submissionItem.idRequiredType == null ? formQuestion.idRequiredType : submissionItem.idRequiredType;
      formQuestion.allowNotes = submissionItem.allowNotes == null ? formQuestion.allowNotes : submissionItem.allowNotes;
      formQuestion.options = submissionItem.options == null ? formQuestion.options : submissionItem.options;
      formQuestion.idParent = submissionItem.idParent == null ? formQuestion.idParent : submissionItem.idParent;
      formQuestion.idItemType = submissionItem.idItemType == null ? formQuestion.idItemType : submissionItem.idItemType;

      if (formQuestion.idItemType == formItemTypes.REPEATER_GROUP.id) {
         try {
            formQuestion.options.isSubform = true;
            formQuestion.options.isSubformLocked = true;
         } catch (ex) {
            //console.log('Could not set');
         }
      }

      formQuestion.state = submissionItem.state == null ? formQuestion.state : submissionItem.state;
      if (typeof submissionItem.response !== 'undefined' || typeof formQuestion.response !== 'undefined') {
         formQuestion.response = typeof submissionItem.response !== 'undefined' ? submissionItem.response : formQuestion.response;
      }
      formQuestion.id = submissionItem.id;
      formQuestion.idParent = submissionItem.idParent;

      formQuestion.score = submissionItem.score == null ? null : submissionItem.score;
      formQuestion.maxScore = submissionItem.maxScore == null ? null : submissionItem.maxScore;
      formQuestion.noOfComments = submissionItem.noOfComments == null ? null : submissionItem.noOfComments;

      formQuestion.flagged = submissionItem.flagged == null ? null : submissionItem.flagged;
      formQuestion.noOfFlags = submissionItem.noOfFlags == null ? null : submissionItem.noOfFlags;
      formQuestion.repeaterMaterialized = submissionItem.repeaterMaterialized === true;

      if (typeof submissionItem.no !== 'undefined') {
         formQuestion.no = submissionItem.no;
      }

      return formQuestion;
   }

   buildQuestion({ questions, formItem, submissionItem, resourceMedia, datasourceData, idRepeaterToIgnore = null }) {
      const now = moment.utc().toDate();

      const itemDatasourceData = Array.isArray(datasourceData)
         ? formItem.options?.binding
            ? _.find(datasourceData, (d) => d.noDataSource === formItem.options.binding.noDataSource)?.rows ?? []
            : []
         : [];

      const itemResourceMedia = formItem.resources
         ? _.filter(resourceMedia, (rm) => {
              return _.some(formItem.resources, (ir) => {
                 return ir.noResource == rm.noResource;
              });
           })
         : [];

      let idParentForm = formItem.idParent || null;
      let idParent = null;
      let parentQuestion = null;
      if (idParentForm != null) {
         parentQuestion = _.find(questions, (x) => {
            return x.idFormItem === idParentForm;
         });
         if (parentQuestion) {
            idParent = parentQuestion.id;
         }
      }

      // Stop questions nested in repeaters coming from forms (ie not submissions) from being added
      if (idRepeaterToIgnore !== idParentForm && idParentForm != null && submissionItem == null) {
         // if our parent is not in the tree... Most probably taken out as it was in a repeater
         if (parentQuestion == null) {
            return null;
         }
         // take this question out if parent is a repeater
         if (parentQuestion.idItemType == formItemTypes.REPEATER.id) {
            return null;
         }
      }

      var o = {
         idItemType: formItem.idItemType,
         idFormItem: formItem.id,
         state: 0,
         label: formItem.label,
         hint: formItem.hint || null,
         idRequiredType: formItem.idRequiredType,
         allowNotes: formItem.allowNotes,
         resources: itemResourceMedia,
         idParent: idParent,
         id: newGuid()
      };

      const no = submissionItem ? (submissionItem.response ? submissionItem.response.no : 1) : 1;
      const upd = submissionItem ? (submissionItem.response ? submissionItem.response.upd : now) : now;

      switch (formItem.idItemType) {
         case formItemTypes.TODO.id:
            {
               const val = submissionItem ? (submissionItem.response ? submissionItem.response.val : false) : false;
               o.response = { no, val, upd };
            }
            break;
         case formItemTypes.NUMBER.id:
            {
               const val = submissionItem ? (submissionItem.response ? submissionItem.response.val : null) : null;
               o.response = val != null ? { no, val, upd } : null;
               o.options = { ...formItem.options };
            }
            break;
         case formItemTypes.DATETIME.id:
            {
               const val = submissionItem ? (submissionItem.response ? submissionItem.response.val : null) : null;
               o.response = val != null ? { no, val, upd } : null;
               o.options = { ...formItem.options };
            }
            break;
         case formItemTypes.TEXT.id:
            {
               const val = submissionItem ? (submissionItem.response ? submissionItem.response.val : null) : null;
               o.response = val != null ? { no, val, upd } : null;
            }
            break;
         case formItemTypes.OPTIONS.id:
            {
               o.response = submissionItem ? (submissionItem.response ? submissionItem.response : []) : [];
               o.options = { ...formItem.options };
               if (!o.options.selectOptions) {
                  o.options.selectOptions = itemDatasourceData;
               }
            }
            break;

         case formItemTypes.CALCULATED.id:
            {
               // Display-only derived field. No response persisted.
               o.response = null;

               // IMPORTANT: keep calculated config (options.calculated)
               o.options = { ...formItem.options };

               // If calculated lookup needs datasource rows at runtime:
               // - in your current design, it resolves from repeater.options.listOptions,
               //   so you don't need to inject anything here.
               // - If you later add calculated.lookup.noDataSource, you'd handle it here.
            }
            break;

         case formItemTypes.PHOTO.id:
            o.response = submissionItem ? (submissionItem.response ? submissionItem.response : []) : [];
            o.options = { ...formItem.options };
            break;

         case formItemTypes.SECTION.id:
            o.options = { ...formItem.options };
            break;

         case formItemTypes.REPEATER.id:
            o.options = { ...formItem.options };
            if (
               !o.options.listOptions &&
               classifyRepeaterEntryMode.matchesCanonicalId(o.options.idRepeaterType, repeaterFormItemTypes.LIST.id)
            ) {
               o.options.listOptions = itemDatasourceData;
            }
            break;

         case formItemTypes.REPEATER_GROUP.id:
            o.options = {
               isSubform: true,
               isSubformLocked: true
            };
            o.response = null;
            break;
         default:
            o.response = null;
            break;
      }

      return o;
   }

   guards({ noLocation, formSnapshot, submissionSnapshot, resources, locations }) {
      if (!noLocation) {
         throw new Error('noLocation cannot be null');
      }

      if (typeof formSnapshot === 'undefined') {
         throw new Error('formSnapShot must be provided');
      }

      if (typeof submissionSnapshot === 'undefined') {
         throw new Error('submissionSnapshot must be provided');
      }

      if (typeof resources === 'undefined') {
         throw new Error('resources must be provided');
      }
      if (!_.isArray(resources)) {
         return 'resources should be an array';
      }

      if (typeof locations === 'undefined') {
         throw new Error('locations must be provided');
      }
      if (!_.isArray(locations)) {
         return 'locations should be an array';
      }
   }
}

QuestionBuilder.$Inject = ['ITerritoryDomainService'];

module.exports = QuestionBuilder;
